import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { useAuth } from "../../context/AuthContext";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { differenceInDays, isBefore, parseISO, format } from "date-fns";
import { yupResolver } from "@hookform/resolvers/yup";
import DateSelector from "../../components/common/DateSelector";
import { showError } from "../../components/common/ToastNotification";
import { showSuccess } from "../../components/common/ToastNotification";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";
import InputsNew from "../../components/common/InputsNew";
import InputsDropdown from "../../components/common/InputsDropdown";
import { EMPLOYEES_BY_AGENCY_ID } from "../../api/query";
import { useQuery, useMutation } from "@apollo/client";
import Loader from "../../components/common/Loader";
import {
  SAVE_RESOURCE_WORKING_DATES,
  UPDATE_RESOURCE_WORKING_DATE,
} from "../../api/mutation";

/**
 * ---- Helpers -------------------------------------------------------------
 */

// Converts 12-hour (hh, mm, AM/PM) into a 24-hour "HH:mm:ss" string.
const to24HourTime = (
  hour: string | number,
  minute: string | number,
  period: string,
) => {
  let h = parseInt(String(hour), 10);
  const m = parseInt(String(minute), 10);
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

// react-hook-form + yup.date() cast start_date/end_date into real Date objects
// by the time onSubmit runs, but edit-mode prefill passes plain ISO strings.
// This normalizes either into a Date.
const toDateObj = (value: string | Date) =>
  value instanceof Date ? value : parseISO(value);

// Pulls out yyyy-MM-dd using LOCAL date parts (date-fns' format uses local time,
// so this doesn't shift a day the way toISOString() would for timezones like IST).
const getDatePart = (dateValue: string | Date) =>
  format(toDateObj(dateValue), "yyyy-MM-dd");

// Combines a date (Date object or yyyy-MM-dd/ISO string) with a 12-hour time
// into one local datetime string ("yyyy-MM-ddTHH:mm:ss", no Z -> parsed as local).
const combineDateAndTime = (
  dateValue: string | Date,
  hour: string | number,
  minute: string | number,
  period: string,
) => {
  const datePart = getDatePart(dateValue);
  const timePart = to24HourTime(hour, minute, period);
  return `${datePart}T${timePart}`;
};

// Splits an existing ISO datetime back into date + 12-hour time parts (used for edit mode).
const splitDateTime = (isoValue: string) => {
  const d = new Date(isoValue);
  let hour = d.getHours();
  const minute = d.getMinutes();
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return {
    date: isoValue.split("T")[0],
    hour: String(hour).padStart(2, "0"),
    minute: String(minute).padStart(2, "0"),
    period,
  };
};

const timeFieldSchema = yup
  .number()
  .typeError("This field is required")
  .required("This field is required");

const AddTiming = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { userInfo } = useAuth();
  const agencyID = userInfo?.agencies?.id || "";
  const employeeid = userInfo?.uuid; // for created_by

  // If we navigated here with an existing record (edit mode), it comes via
  // route state. One record here = one row in addresourceworkingdate, i.e.
  // one employee — so editRecord.employee_id is a single id, not an array.
  const editRecord = location?.state?.record || null;
  const isEditMode = !!editRecord;

  const [employeeOptions, setEmployeeOptions] = useState<
    { key: string; name: string }[]
  >([]);

  const { loading: employeesDataLoading } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
    variables: {
      agency_id: parseInt(agencyID),
    },
    skip: !agencyID,
    fetchPolicy: "no-cache",
    onCompleted: (data: any) => {
      const options =
        data?.employees_by_agency_id?.employees?.map((emp: any) => ({
          key: emp.employee_id,
          name: `${emp.first_name} ${emp.last_name}`,
        })) || [];

      setEmployeeOptions(options);
    },
  });

  const [selectedEmp, setSelectedEmp] = useState<
    { key: string; name: string }[]
  >([]);

  const [saveResourceWorkingDates, { loading: saving }] = useMutation(
    SAVE_RESOURCE_WORKING_DATES,
  );
  const [updateResourceWorkingDate, { loading: updating }] = useMutation(
    UPDATE_RESOURCE_WORKING_DATE,
  );
  const submitting = saving || updating;

  const resourceOerViewSchema = yup.object().shape({
    start_date: yup
      .date()
      .required("This field is required")
      .typeError("This field is required"),
    end_date: yup
      .date()
      .required("This field is required")
      .typeError("This field is required"),
    startTimeHour: timeFieldSchema
      .min(1, "Hour must be 1-12")
      .max(12, "Hour must be 1-12"),
    startTimeMinute: timeFieldSchema
      .min(0, "Minute must be 0-59")
      .max(59, "Minute must be 0-59"),
    startTimePeriod: yup.string().required("This field is required"),
    endTimeHour: timeFieldSchema
      .min(1, "Hour must be 1-12")
      .max(12, "Hour must be 1-12"),
    endTimeMinute: timeFieldSchema
      .min(0, "Minute must be 0-59")
      .max(59, "Minute must be 0-59"),
    endTimePeriod: yup.string().required("This field is required"),
    notes: yup.string().nullable(),
     isOvernightShift: yup.boolean(),
  });

  const {
    setValue,
    formState: { errors },
    watch,
    trigger,
    control,
    getValues,
    register,
    handleSubmit,
    reset,
  } = useForm<any>({
    resolver: yupResolver(resourceOerViewSchema),
    defaultValues: {
      end_date: "",
      start_date: "",
      startTimeHour: "",
      startTimeMinute: "",
      startTimePeriod: "",
      endTimeHour: "",
      endTimeMinute: "",
      endTimePeriod: "",
      notes: "",
            isOvernightShift: false,
    },
  });

  // ---- Pre-fill the form when editing an existing record --------------
  useEffect(() => {
    if (!editRecord) return;

    if (editRecord.start_date) {
      const s = splitDateTime(editRecord.start_date);
      setValue("start_date", s.date);
      setValue("startTimeHour", s.hour);
      setValue("startTimeMinute", s.minute);
      setValue("startTimePeriod", s.period);
    }
    if (editRecord.end_date) {
      const e = splitDateTime(editRecord.end_date);
      setValue("end_date", e.date);
      setValue("endTimeHour", e.hour);
      setValue("endTimeMinute", e.minute);
      setValue("endTimePeriod", e.period);
    }
    if (editRecord.notes) {
      setValue("notes", editRecord.notes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRecord]);


  // Employee selection for edit mode needs the options list loaded first,
  // so we match once employeeOptions has arrived. editRecord.employee_id is
  // a single id (one row = one employee), not an array.
  useEffect(() => {
    if (!editRecord || employeeOptions.length === 0) return;
    const matched = employeeOptions.filter(
      (opt) => String(opt.key) === String(editRecord.employee_id),
    );
    if (matched.length) setSelectedEmp(matched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRecord, employeeOptions]);

  const onSubmit = async (data: any) => {
    // -- Basic validations not covered by yup -------------------------
    if (selectedEmp.length === 0) {
      showError("Please select at least one employee.");
      return;
    }

    if (isEditMode && selectedEmp.length > 1) {
      showError(
        "This is a single record — please select only one employee when editing.",
      );
      return;
    }

    const startDateTime = combineDateAndTime(
      data.start_date,
      data.startTimeHour,
      data.startTimeMinute,
      data.startTimePeriod,
    );
    const endDateTime = combineDateAndTime(
      data.end_date,
      data.endTimeHour,
      data.endTimeMinute,
      data.endTimePeriod,
    );

    const startDateObj = new Date(startDateTime);
    const endDateObj = new Date(endDateTime);

    if (isBefore(endDateObj, startDateObj) || endDateObj.getTime() === startDateObj.getTime()) {
      showError("End date/time must be after start date/time.");
      return;
    }

    if (
      differenceInDays(toDateObj(data.end_date), toDateObj(data.start_date)) > 7
    ) {
      showError("The selected date range cannot exceed 7 days.");
      return;
    }

    // Time can't be more than 11:59 -> enforced by hour(1-12)/minute(0-59) schema,
    // this is just an extra safety net.
    const invalidTime = [
      [data.startTimeHour, data.startTimeMinute],
      [data.endTimeHour, data.endTimeMinute],
    ].some(([h, m]) => {
      const hh = parseInt(h, 10);
      const mm = parseInt(m, 10);
      return hh < 1 || hh > 12 || mm < 0 || mm > 59;
    });
    if (invalidTime) {
      showError("Time cannot be more than 11:59.");
      return;
    }

    try {
      if (isEditMode) {
        // -- Update a single existing record by id -----------------------
        const res = await updateResourceWorkingDate({
          variables: {
            id: parseInt(editRecord.id),
            employeeId: parseInt(selectedEmp[0].key, 10),
            startDate: startDateTime,
            endDate: endDateTime,
            notes: data.notes || "",
          },
        });

        const result = res?.data?.update_resource_working_date;
        if (result?.status === "Success") {
          showSuccess?.("Record updated successfully.");
          navigate(-1);
        } else {
          showError("Failed to update record.");
        }
        return;
      }

      // -- Create/auto-upsert one record per selected employee -----------
      const res = await saveResourceWorkingDates({
        variables: {
          employees: selectedEmp.map((emp) => ({
            employee_id: parseInt(emp.key, 10),
          })),
          startDate: startDateTime,
          endDate: endDateTime,
          notes: data.notes || "",
          createdBy: employeeid ? String(employeeid) : undefined,
           is_overnight: data.isOvernightShift,
        },
      });

      const result = res?.data?.save_resource_working_dates;
      if (result?.status === "Success") {
        showSuccess?.("Saved successfully.");
        navigate(-1);
      } else {
        showError("Failed to save records.");
      }
    } catch (err: any) {
      showError("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <form
        id="cts-respon-p-dv"
        onSubmit={handleSubmit(onSubmit)}
        className="w-full  relative addtiming-form-dv  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb />
          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              {isEditMode ? "Update Resource" : "Add Resource"}
            </h3>
          </div>
          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <p className="text-sm dark:text-white mt-0">Fill in the details to add a new resource</p>
            <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
              <div className="grid">
                <InputsDropdownMultipleSearchSelect
                  label="Employee Name*"
                  placeHolder="Choose"
                  options={employeeOptions}
                  onSelect={(key: any, name: any) => {
                    if (key === "all") {
                      setSelectedEmp(
                        name
                          ? []
                          : employeeOptions.map((s) => ({
                              key: s.key,
                              name: s.name,
                            })),
                      );
                      return;
                    }
                    setSelectedEmp((prev) => {
                      const exists = prev.some((s) => s.key == key);
                      if (exists) return prev.filter((s) => s.key != key);
                      return [...prev, { key, name }];
                    });
                  }}
                  inputType="checkbox"
                  checkedItems={selectedEmp}
                  disableDropdown={isEditMode}
                />
                {selectedEmp.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedEmp.map((s) => (
                      <div
                        key={s.key}
                        className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                      >
                        <span className="text-sm dark:text-white">
                          {s.name}
                        </span>
                        {!isEditMode && (
                          <img
                            className="h-4 w-4 cursor-pointer"
                            src={closeDowpDowniconimg}
                            alt="Remove"
                            onClick={() =>
                              setSelectedEmp((prev) =>
                                prev.filter((x) => x.key !== s.key),
                              )
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
              <div className="status-filter pb-4 grid gap-[8px]">
                <Controller
                  name="start_date"
                  control={control}
                  render={({ field }) => (
                    <DateSelector
                      label="Start Date*"
                      selectedDate={field.value}
                      onChange={(date: string | any) => {
                        field.onChange(date);
                        setValue("end_date", null);
                        setValue("start_date", date);
                        trigger("start_date");
                      }}
                      nothideParsedValue={true}
                      blueIcon={true}
                      error={errors.start_date?.message}
                      showAllDate={true}
                      disabled={isEditMode}
                    />
                  )}
                />
              </div>
              <div className="status-filter pb-4 grid gap-[8px]">
                <Controller
                  name="end_date"
                  control={control}
                  render={({ field }) => (
                    <DateSelector
                      label="End Date*"
                      selectedDate={field.value}
                      onChange={(date: string | null | any) => {
                        const date2 = parseISO(watch("start_date"));
                        const date1 = parseISO(date);
                        if (isBefore(date1, date2)) {
                          showError(
                            "Please select date greater than start date.",
                          );
                        } else if (differenceInDays(date1, date2) > 7) {
                          showError(
                            "The selected date range cannot exceed 7 days.",
                          );
                        } else {
                          field.onChange(date);
                          setValue("end_date", date);
                          trigger("end_date");
                        }
                      }}
                      nothideParsedValue={true}
                      blueIcon={true}
                      dontopenDatepicker={!watch("start_date")}
                      error={errors.end_date?.message}
                      showAllDate={true}
                      disabled={isEditMode}
                    />
                  )}
                />
              </div>

              <div className="flex flex-col gap-2 starttime-dv-item">
                <label className="text-sm font-semibold dark:text-white">
                  Start Time*
                </label>
                <div className="flex items-center gap-[8px] inner-starttime-dv-item">
                  <div className="flex-1 max-w-[90px]">
                    <InputsNew
                      label=""
                      contentPlaceholder="HH"
                      type="number"
                      register={register("startTimeHour", {
                        min: 1,
                        max: 12,
                      })}
                      errorMessage1={errors?.startTimeHour}
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex-1 max-w-[90px]">
                    <InputsNew
                      label=""
                      contentPlaceholder="MM"
                      type="number"
                      register={register("startTimeMinute", {
                        min: 0,
                        max: 59,
                      })}
                      errorMessage1={errors?.startTimeMinute}
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex-1 max-w-[90px]">
                    <InputsDropdown
                      label={""}
                      placeHolder={"Select"}
                      options={[
                        {
                          key: "AM",
                          name: "AM",
                        },
                        {
                          key: "PM",
                          name: "PM",
                        },
                      ]}
                      onSelect={(v1: any) => {
                        setValue("startTimePeriod", v1);
                        trigger("startTimePeriod");
                      }}
                      errorMessage1={errors.startTimePeriod}
                      initialValue={watch("startTimePeriod")}
                    />
                  </div>
                </div>
              </div>

              {/* End Time (Hour / Minute / AM-PM) */}
              <div className="flex flex-col gap-2 starttime-dv-item">
                <label className="text-sm font-semibold dark:text-white">
                  End Time*
                </label>
                <div className="flex items-center gap-[8px] inner-starttime-dv-item">
                  <div className="flex-1 max-w-[90px]">
                    <InputsNew
                      label=""
                      contentPlaceholder="HH"
                      type="number"
                      register={register("endTimeHour", {
                        min: 1,
                        max: 12,
                      })}
                      errorMessage1={errors?.endTimeHour}
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex-1 max-w-[90px]">
                    <InputsNew
                      label=""
                      contentPlaceholder="MM"
                      type="number"
                      register={register("endTimeMinute", {
                        min: 0,
                        max: 59,
                      })}
                      errorMessage1={errors?.endTimeMinute}
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex-1 max-w-[90px]">
                    <InputsDropdown
                      label={""}
                      placeHolder={"Select"}
                      options={[
                        {
                          key: "AM",
                          name: "AM",
                        },
                        {
                          key: "PM",
                          name: "PM",
                        },
                      ]}
                      onSelect={(v1: any) => {
                        setValue("endTimePeriod", v1);
                        trigger("endTimePeriod");
                      }}
                      errorMessage1={errors.endTimePeriod}
                      initialValue={watch("endTimePeriod")}
                    />
                  </div>
                </div>
              </div>
            </div>
            {
              !isEditMode &&  <div className="flex flex-col gap-2 mt-2">
                <label className="text-sm font-semibold dark:text-white">
                  Is Overnight Shift:
                </label>

                <Controller
                  name="isOvernightShift"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="true"
                          checked={field.value === true}
                          onChange={() => field.onChange(true)}
                        />
                        <span className="text-sm dark:text-white">Yes</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="false"
                          checked={field.value === false}
                          onChange={() => field.onChange(false)}
                        />
                        <span className="text-sm dark:text-white">No</span>
                      </label>
                    </div>
                  )}
                />
              </div>
            }
             

            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
              <InputsNew
                contentPlaceholder="Notes"
                label="Add Notes"
                type="textarea"
                register={register("notes")}
                errorMessage={errors.notes}
              />
            </div>
          </div>

          <div className="self-stretch flex flex-row items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
            >
              {submitting ? "Saving..." : isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </form>
      {employeesDataLoading && <Loader />}
    </>
  );
};

export default AddTiming;
